import { useEffect, useRef, useState } from 'react'
import { Play, VolumeX } from 'lucide-react'
import { MOBILE_APP_POSTER } from '@/lib/mobile-app'
import { cn } from '@/lib/utils'

// The app preview video. It plays our own file (lib/mobile-app.ts, hosted on R2), so no third party sees the visit
// and YouTube cannot show its "Sign in to confirm you're not a bot" screen in the frame.
//
// It shows a local poster and loads no video until it starts. It starts by itself when half of it scrolls
// into view, and never for a visitor who asks for reduced motion. The play button always works.
//
// Sound: the browser decides, not the page. Chrome, Firefox and Safari allow autoplay with sound only after
// the visitor tapped or clicked on the site (Chrome on desktop also after enough earlier viewing with sound).
// So the player asks for sound first. When the browser refuses, it plays muted and shows a "Tap for sound"
// button, and the first tap, click or key press on the page while the video is on screen turns sound on.
// It pauses when it scrolls out of view, so sound never plays under other content.
const START_RATIO = 0.5
const PAUSE_BELOW = 0.2

export function PromoVideo({ src, caption, className }: { src: string; caption?: string; className?: string }) {
  const [playing, setPlaying] = useState(false)
  // True while the video plays without sound: the button is shown, and a tap on the page turns sound on.
  const [silent, setSilent] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // The visitor can scroll away and back. Only a pause made by scrolling away is undone by scrolling back.
  const pausedByScroll = useRef(false)
  const visible = useRef(false)

  // Start by itself when half of the player is on screen.
  useEffect(() => {
    const root = rootRef.current
    if (!root || playing || typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPlaying(true)
          io.disconnect()
        }
      },
      { threshold: START_RATIO },
    )
    io.observe(root)
    return () => io.disconnect()
  }, [playing])

  // Once playing: ask for sound, fall back to muted, follow the volume, and pause out of view.
  useEffect(() => {
    const video = videoRef.current
    const root = rootRef.current
    if (!playing || !video || !root) return

    const onVolume = () => setSilent(video.muted || video.volume === 0)
    video.addEventListener('volumechange', onVolume)

    video.muted = false
    Promise.resolve(video.play())
      .then(() => setSilent(video.muted))
      .catch(() => {
        // Autoplay with sound was refused: play muted, which every browser allows.
        video.muted = true
        setSilent(true)
        Promise.resolve(video.play()).catch(() => {})
      })

    let io: IntersectionObserver | undefined
    if (typeof IntersectionObserver !== 'undefined') {
      visible.current = true
      io = new IntersectionObserver(
        (entries) => {
          const ratio = entries[entries.length - 1]?.intersectionRatio ?? 0
          visible.current = ratio >= PAUSE_BELOW
          if (ratio < PAUSE_BELOW && !video.paused) {
            pausedByScroll.current = true
            video.pause()
          } else if (ratio >= START_RATIO && pausedByScroll.current) {
            pausedByScroll.current = false
            Promise.resolve(video.play()).catch(() => {})
          }
        },
        { threshold: [0, PAUSE_BELOW, START_RATIO] },
      )
      io.observe(root)
    }

    return () => {
      video.removeEventListener('volumechange', onVolume)
      io?.disconnect()
    }
  }, [playing])

  // A tap, click or key press is the signal browsers accept for sound. While the video plays muted and is on
  // screen, the first one turns the sound on.
  useEffect(() => {
    if (!playing || !silent) return
    const unlock = () => {
      const video = videoRef.current
      if (!video || !visible.current) return
      video.muted = false
      setSilent(false)
    }
    const events = ['click', 'keydown', 'touchend'] as const
    for (const e of events) document.addEventListener(e, unlock, { once: true })
    return () => {
      for (const e of events) document.removeEventListener(e, unlock)
    }
  }, [playing, silent])

  function turnSoundOn() {
    const video = videoRef.current
    if (!video) return
    video.muted = false
    if (video.volume === 0) video.volume = 1
    setSilent(false)
    if (video.paused) Promise.resolve(video.play()).catch(() => {})
  }

  return (
    <div ref={rootRef} className={cn('min-w-0', className)}>
      <div className="relative aspect-video w-full overflow-hidden rounded-frame border border-border bg-card shadow-raised">
        {playing ? (
          <>
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full bg-black"
              src={src}
              poster={MOBILE_APP_POSTER}
              aria-label="Uptrack app preview"
              playsInline
              controls
              preload="auto"
            />
            {silent && (
              <button
                type="button"
                onClick={turnSoundOn}
                className="absolute left-3 top-3 z-10 flex items-center gap-2 rounded-md bg-black/80 px-3 py-2 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <VolumeX className="h-4 w-4" aria-hidden="true" />
                Tap for sound
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Play the Uptrack app preview video"
          >
            <img
              src={MOBILE_APP_POSTER}
              alt=""
              width={1280}
              height={720}
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-black shadow-raised">
                <Play className="ml-1 h-7 w-7" aria-hidden="true" />
              </span>
            </span>
          </button>
        )}
      </div>
      {caption && <p className="mt-3 text-sm text-body">{caption}</p>}
    </div>
  )
}
